import React from 'react';
// eslint-disable-next-line import/no-webpack-loader-syntax
import mapboxgl from '!mapbox-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import * as turf from '@turf/turf';
import Swal from 'sweetalert2';
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import { checkAccess } from '../../utils/roleFunctions';
import { ACCESS, ACCESS_SECTIONS } from '../../utils/constants/access';
import { getAPIDataParamsReturn, getAPIDataReturn, postApi, postApiJson, putApiJson } from '../../utils/apiFunction';
import SelectFarmWarehouse from '../../components/SelectFarmWarehouse';
const REQUIRED_ACCESS = { key: ACCESS_SECTIONS.OYSTERMATE, value: ACCESS.EDIT };
const EMPTY_FC = { type: 'FeatureCollection', features: [] };
const MAPBOX_TOKEN = 'pk.eyJ1IjoidWdseWJ1bnlpcCIsImEiOiJjbGZvNnZrbHYwajMyNDJwdGk0MHZlYWdlIn0.m2XdPYhVDEe00Zt4EcWk9g';

const drawStyles = [
    {
        id: 'line-designer-line',
        type: 'line',
        filter: ['all', ['==', '$type', 'LineString'], ['!=', 'mode', 'static']],
        paint: {
            'line-color': '#0a66c2',
            'line-width': 4
        }
    },
    {
        id: 'line-designer-line-static',
        type: 'line',
        filter: ['all', ['==', '$type', 'LineString'], ['==', 'mode', 'static']],
        paint: {
            'line-color': '#0a66c2',
            'line-width': 4
        }
    },
    {
        id: 'line-designer-vertex',
        type: 'circle',
        filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'vertex']],
        paint: {
            'circle-radius': 4,
            'circle-color': '#ff7043'
        }
    }
];

const LineDesigner = ({ access }) => {
    const canAccess = checkAccess(access, REQUIRED_ACCESS);

    const mapNodeRef = React.useRef(null);
    const mapRef = React.useRef(null);
    const drawRef = React.useRef(null);
    const selectedFeatureIdRef = React.useRef(null);
    const updateMetricsRef = React.useRef(null);
    const handleSelectLineRef = React.useRef(null);

    const [mapReady, setMapReady] = React.useState(false);
    const [selectedFarm, setSelectedFarm] = React.useState(null);
    const [leases, setLeases] = React.useState([]);
    const [infrastructure, setInfrastructure] = React.useState([]);
    const [selectedLeaseId, setSelectedLeaseId] = React.useState('');
    const [selectedInfraId, setSelectedInfraId] = React.useState('');
    const [lineNbr, setLineNbr] = React.useState('');
    const [lineName, setLineName] = React.useState('');
    const [linesList, setLinesList] = React.useState([]);
    const [lineGeo, setLineGeo] = React.useState(EMPTY_FC);
    const [geoDisplayLines, setGeoDisplayLines] = React.useState(EMPTY_FC);
    const [geoDisplayLabels, setGeoDisplayLabels] = React.useState(EMPTY_FC);
    const [leaseGeo, setLeaseGeo] = React.useState(EMPTY_FC);
    const [selectedLineId, setSelectedLineId] = React.useState(null);
    const [selectedLineStatus, setSelectedLineStatus] = React.useState(null);
    const [metrics, setMetrics] = React.useState(null);
    const [saving, setSaving] = React.useState(false);
    const [loadingLookups, setLoadingLookups] = React.useState(false);
    const [angleInput, setAngleInput] = React.useState('');
    const [lengthInput, setLengthInput] = React.useState('');
    const [offsetDistanceInput, setOffsetDistanceInput] = React.useState('');
    const [offsetBearingInput, setOffsetBearingInput] = React.useState('');
    const [offsetPreview, setOffsetPreview] = React.useState(EMPTY_FC);
    const [startLatInput, setStartLatInput] = React.useState('');
    const [startLngInput, setStartLngInput] = React.useState('');
    const [canEditWarehouse, setCanEditWarehouse] = React.useState(false);

    const selectedInfraUnitWidth = React.useMemo(() => {
        const infra = infrastructure.find(i => String(i.id || i.Id) === String(selectedInfraId));
        return infra ? (infra.unitWidth || infra.UnitWidth || 1) : 1;
    }, [infrastructure, selectedInfraId]);

    React.useEffect(() => {
        if (!canAccess) {
            return;
        }
        setLoadingLookups(true);
        const loadInfrastructure = async () => {
            try {
                const infra = await getAPIDataReturn('/Farms/Lookups/Infrastructure');
                setInfrastructure(infra || []);
            } catch (e) {
                console.error(e);
                Swal.fire('Error', 'Failed to load infrastructure', 'error');
            } finally {
                setLoadingLookups(false);
            }
        };
        loadInfrastructure();
    }, [canAccess]);

    React.useEffect(() => {
        if (!canAccess) {
            return;
        }
        mapboxgl.accessToken = MAPBOX_TOKEN;
        var lng = 150.170690043914;
        var lat = -35.719974685415416;//cranbrook
        const map = new mapboxgl.Map({
            container: mapNodeRef.current,
            style: 'mapbox://styles/mapbox/satellite-streets-v11',
            center: [lng, lat],
            zoom: 8
        });
        map.addControl(new mapboxgl.NavigationControl(), 'top-right');

        const draw = new MapboxDraw({
            displayControlsDefault: false,
            controls: {
                line_string: true,
                trash: true
            },
            styles: drawStyles,
            defaultMode: 'simple_select'
        });
        map.addControl(draw, 'top-left');

        drawRef.current = draw;
        mapRef.current = map;

        map.on('error', (e) => console.error('Mapbox error', e?.error || e));
        map.on('load', () => console.log('LineDesigner map load fired'));

        // Expose for quick console debugging when drag pan gets stuck
        if (typeof window !== 'undefined') {
            window.ldMap = map;
            window.ldDraw = draw;
        }

        const ensureDragPanEnabled = () => {
            const m = mapRef.current;
            if (m?.dragPan && !m.dragPan.isEnabled()) {
                m.dragPan.enable();
            }
        };

        const onDrawChange = () => {
            const data = draw.getAll();
            if (!data || !data.features || data.features.length === 0) {
                setMetrics(null);
                setSelectedLineId(null);
                selectedFeatureIdRef.current = null;
                return;
            }
            const feature = data.features[0];
            selectedFeatureIdRef.current = feature.id;
            updateMetricsRef.current?.(feature);
        };

        const enforceTwoPointLine = () => {
            if (!drawRef.current) return;
            const mode = drawRef.current.getMode ? drawRef.current.getMode() : null;
            if (mode !== 'draw_line_string') return;
            const data = drawRef.current.getAll();
            if (!data?.features || data.features.length === 0) return;
            const activeFeature = data.features[data.features.length - 1];
            const coords = activeFeature?.geometry?.coordinates || [];
            console.log('enforceTwoPointLine', coords);
            if (coords.length >= 3) {
                drawRef.current.changeMode('simple_select', { featureIds: [activeFeature.id] });
            }
        };

        map.on('draw.create', onDrawChange);
        map.on('draw.update', onDrawChange);
        map.on('draw.delete', () => {
            setMetrics(null);
            setSelectedLineId(null);
        });

        // Mapbox Draw can leave dragPan disabled after drawing; re-enable on mode changes
        map.on('draw.modechange', ensureDragPanEnabled);
        map.on('draw.selectionchange', ensureDragPanEnabled);
        map.on('click', ensureDragPanEnabled);

        map.on('load', () => {
            map.addSource('existing-lines', {
                type: 'geojson',
                data: lineGeo
            });

            map.addSource('leases-geo', {
                type: 'geojson',
                data: leaseGeo
            });

            map.addLayer({
                id: 'existing-lines-fill',
                type: 'fill',
                source: 'existing-lines',
                paint: {
                    'fill-color': '#1fbba6',
                    'fill-opacity': 0.25
                }
            });

            map.addLayer({
                id: 'existing-lines-outline',
                type: 'line',
                source: 'existing-lines',
                paint: {
                    'line-color': '#0b7c6d',
                    'line-width': 2
                }
            });

            map.addLayer({
                id: 'existing-lines-selected',
                type: 'line',
                source: 'existing-lines',
                filter: ['==', ['to-string', ['get', 'LineId']], ''],
                paint: {
                    'line-color': '#ff9800',
                    'line-width': 5
                }
            });

            map.addLayer({
                id: 'lease-outlines',
                type: 'line',
                source: 'leases-geo',
                paint: {
                    'line-color': '#1800ee',
                    'line-width': 2,
                    'line-dasharray': [2, 2],
                    'line-opacity': 0.9
                }
            });

            map.addSource('unit-lines-geo', {
                type: 'geojson',
                data: geoDisplayLines
            });

            map.addLayer({
                id: 'unit-lines-geo-fill',
                type: 'fill',
                source: 'unit-lines-geo',
                filter: ['==', '$type', 'Polygon'],
                paint: {
                    'fill-color': '#90a4ae',
                    'fill-opacity': 0.25
                }
            });

            map.addLayer({
                id: 'unit-lines-geo-outline',
                type: 'line',
                source: 'unit-lines-geo',
                paint: {
                    'line-color': '#607d8b',
                    'line-width': 2
                }
            });

            map.addSource('unit-labels-geo', {
                type: 'geojson',
                data: geoDisplayLabels
            });

            map.addLayer({
                id: 'unit-labels-geo-layer',
                type: 'symbol',
                source: 'unit-labels-geo',
                layout: {
                    'text-field': ['get', 'LineNbr'],
                    'text-size': 12,
                    'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold']
                },
                paint: {
                    'text-color': '#263238',
                    'text-halo-color': '#ffffff',
                    'text-halo-width': 1
                },
                minzoom: 14
            });

            // Keep editable draw layers on top of existing line/label layers
            const drawLayers = ['line-designer-vertex', 'line-designer-line', 'line-designer-line-static'];
            const existingLayers = ['existing-lines-selected', 'existing-lines-outline', 'existing-lines-fill', 'unit-lines-geo-fill', 'unit-lines-geo-outline', 'unit-labels-geo-layer', 'lease-outlines'];
            // First try explicit ordering before existing layers
            drawLayers.forEach(drawLayerId => {
                existingLayers.forEach(existingLayerId => {
                    if (map.getLayer(drawLayerId) && map.getLayer(existingLayerId)) {
                        map.moveLayer(drawLayerId, existingLayerId);
                    }
                });
            });
            // Then ensure draw layers are last (top) as a fallback
            drawLayers.forEach(drawLayerId => {
                if (map.getLayer(drawLayerId)) {
                    map.moveLayer(drawLayerId);
                }
            });

            const handleLineClick = (e) => {
                if (!e.features || e.features.length === 0) return;
                const feature = e.features[0];
                const lineId = feature?.properties?.LineId;
                if (lineId) {
                    handleSelectLineRef.current?.(String(lineId), feature);
                }
            };


            const handleOtherClick = (e) => {
                if (!e.features || e.features.length === 0) return;
                const feature = e.features[0];
                const lineId = feature?.properties?.LineId;
                if (!lineId) return;
                // Try to find the matching line feature with geometry
                const map = mapRef.current;
                let lineFeature = null;
                if (map?.getSource('existing-lines')) {
                    const queried = map.querySourceFeatures('existing-lines', {
                        filter: ['==', ['to-string', ['get', 'LineId']], String(lineId)]
                    });
                    if (queried && queried.length > 0) {
                        lineFeature = queried[0];
                    }
                }
                handleSelectLineRef.current?.(String(lineId), lineFeature || feature);
            };

            map.on('click', 'existing-lines-outline', handleLineClick);
            map.on('click', 'existing-lines-fill', handleLineClick);
            map.on('click', 'unit-lines-geo-outline', handleOtherClick);
            map.on('click', 'unit-lines-geo-fill', handleOtherClick);
            map.on('click', 'unit-labels-geo-layer', handleOtherClick);
            map.on('click', enforceTwoPointLine);
            map.on('mouseenter', 'existing-lines-outline', () => { map.getCanvas().style.cursor = 'pointer'; });
            map.on('mouseleave', 'existing-lines-outline', () => { map.getCanvas().style.cursor = ''; });
            map.on('mouseenter', 'existing-lines-fill', () => { map.getCanvas().style.cursor = 'pointer'; });
            map.on('mouseleave', 'existing-lines-fill', () => { map.getCanvas().style.cursor = ''; });
            map.on('mouseenter', 'unit-lines-geo-outline', () => { map.getCanvas().style.cursor = 'pointer'; });
            map.on('mouseleave', 'unit-lines-geo-outline', () => { map.getCanvas().style.cursor = ''; });
            map.on('mouseenter', 'unit-lines-geo-fill', () => { map.getCanvas().style.cursor = 'pointer'; });
            map.on('mouseleave', 'unit-lines-geo-fill', () => { map.getCanvas().style.cursor = ''; });
            map.on('mouseenter', 'unit-labels-geo-layer', () => { map.getCanvas().style.cursor = 'pointer'; });
            map.on('mouseleave', 'unit-labels-geo-layer', () => { map.getCanvas().style.cursor = ''; });

            map.addSource('offset-preview', {
                type: 'geojson',
                data: offsetPreview
            });

            map.addLayer({
                id: 'offset-preview-line',
                type: 'line',
                source: 'offset-preview',
                layout: {
                    'line-cap': 'round',
                    'line-join': 'round'
                },
                paint: {
                    'line-color': '#9c27b0',
                    'line-width': 2,
                    'line-dasharray': [2, 2]
                }
            });

            setMapReady(true);
        });

        return () => {
            map.remove();
        };
    }, [canAccess]);
    React.useEffect(() => {
        if (mapRef.current) {
            window.ldMap = mapRef.current;
            window.ldDraw = drawRef.current;
            console.log('LineDesigner debug refs set');
        }
    }, [mapReady]);
    React.useEffect(() => {
        if (mapReady && mapRef.current && mapRef.current.getSource('existing-lines')) {
            mapRef.current.getSource('existing-lines').setData(lineGeo || EMPTY_FC);
        }
    }, [lineGeo, mapReady]);

    React.useEffect(() => {
        if (mapReady && mapRef.current && mapRef.current.getSource('leases-geo')) {
            mapRef.current.getSource('leases-geo').setData(leaseGeo || EMPTY_FC);
        }
    }, [leaseGeo, mapReady]);

    React.useEffect(() => {
        if (mapReady && mapRef.current && mapRef.current.getSource('unit-lines-geo')) {
            mapRef.current.getSource('unit-lines-geo').setData(geoDisplayLines || EMPTY_FC);
        }
    }, [geoDisplayLines, mapReady]);

    React.useEffect(() => {
        if (mapReady && mapRef.current && mapRef.current.getSource('unit-labels-geo')) {
            mapRef.current.getSource('unit-labels-geo').setData(geoDisplayLabels || EMPTY_FC);
        }
    }, [geoDisplayLabels, mapReady]);

    React.useEffect(() => {
        if (mapReady && mapRef.current && mapRef.current.getSource('offset-preview')) {
            mapRef.current.getSource('offset-preview').setData(offsetPreview || EMPTY_FC);
        }
    }, [offsetPreview, mapReady]);

    React.useEffect(() => {
        if (metrics) {
            setAngleInput(metrics.angle !== undefined ? metrics.angle.toFixed(2) : '');
            setLengthInput(metrics.units !== undefined ? String(metrics.units) : '');
            setStartLngInput(metrics.start && metrics.start[0] !== undefined ? String(metrics.start[0]) : '');
            setStartLatInput(metrics.start && metrics.start[1] !== undefined ? String(metrics.start[1]) : '');
        } else {
            setAngleInput('');
            setLengthInput('');
            setStartLngInput('');
            setStartLatInput('');
        }
    }, [metrics]);

    React.useEffect(() => {
        if (!mapReady || !mapRef.current || !mapRef.current.getLayer('existing-lines-selected')) {
            return;
        }
        const idFilter = selectedLineId ? String(selectedLineId) : '';
        mapRef.current.setFilter('existing-lines-selected', ['==', ['to-string', ['get', 'LineId']], idFilter]);
    }, [selectedLineId, mapReady]);

    React.useEffect(() => {
        if (!selectedFarm?.warehouseId) {
            setLeaseGeo(EMPTY_FC);
            setCanEditWarehouse(false);
            return;
        }
        const warehouseId = selectedFarm.warehouseId;
        setSelectedLeaseId('');
        setLinesList([]);
        clearAllInputs();



        const loadLeases = async () => {
            try {
                const leaseData = await getAPIDataParamsReturn('/Farms/Lookups/Leases', { WarehouseId: warehouseId });
                setLeases(leaseData || []);
                if (leaseData && leaseData.length > 0) {
                    setSelectedLeaseId(String(leaseData[0].id || leaseData[0].Id));
                    flyToLease(leaseData[0]);
                }
                loadLeaseOutlines(leaseData);
            } catch (e) {
                console.error(e);
                Swal.fire('Error', 'Unable to load leases for this warehouse.', 'error');
            }
        };
        loadLeases();
    }, [selectedFarm]);

    React.useEffect(() => {
        if (!selectedLeaseId || !selectedFarm?.warehouseId || !infrastructure) {
            return;
        }
        loadLinesList(selectedLeaseId);
        const lease = leases.find(l => String(l.id || l.Id) === String(selectedLeaseId));
        if (lease) {
            flyToLease(lease);
        }
    }, [selectedLeaseId, infrastructure]);

    const flyToLease = (lease) => {
        if (!mapRef.current || !lease) return;
        const lat = lease.latitude || lease.Latitude;
        const lng = lease.longitute || lease.Longitute;
        if (lat && lng) {
            mapRef.current.flyTo({ center: [lng, lat], zoom: 16 });
        }
    };

    const updateMetricsFromFeature = (feature) => {
        if (!feature || feature.geometry?.type !== 'LineString' || feature.geometry.coordinates.length < 2) {
            setMetrics(null);
            return;
        }
        console.log(infrastructure, selectedInfraId);
        const props = feature.properties || {};
        const coords = feature.geometry.coordinates;
        const start = coords[0];
        const end = coords[coords.length - 1];
        const lengthKm = turf.length(feature, { units: 'kilometers' });
        const meters = Math.round(lengthKm * 1000);
        const bearing = (turf.bearing(start, end) + 360) % 360;
        var infra = infrastructure.find(i => String(i.id || i.Id) === String(selectedInfraId));
        var sizeLen = 1;
        var unitWidth = 1;

        if (infra) {
            sizeLen = infra ? (infra.sizeLength || infra.SizeLength) : 1;
            unitWidth = infra ? (infra.unitWidth || infra.UnitWidth || 1) : 1;
        } else {
            sizeLen = props ? (props.sizeLength || props.SizeLength) : null;
            unitWidth = props ? (props.unitWidth || props.UnitWidth || 1) : 1;
        }
        const units = sizeLen ? Math.max(1, Math.round((meters / sizeLen) * unitWidth)) : 0;
        setMetrics({
            meters,
            units,
            angle: bearing,
            start,
            end
        });

        if (!props.LineNbr) {
            setLineNbr(String(suggestNextLineNbr(linesList)));
        }
    };

    const buildLineGeoFromList = (list) => {
        if (!list || list.length === 0) return EMPTY_FC;
        const features = list
            .filter(l => (l.status ?? l.Status) !== 2)
            .map(l => {
                const derived = deriveCoordsFromLineTable(l);
                if (!derived?.coords) return null;
                var infra = infrastructure.find(i => String(i.id || i.Id) === String(l.infrastructureId || l.InfrastructureId));
                return {
                    type: 'Feature',
                    geometry: { type: 'LineString', coordinates: derived.coords },
                    properties: {
                        LineId: l.id ?? l.Id,
                        LineNbr: l.lineNbr ?? l.LineNbr ?? '',
                        Name: l.name ?? l.Name ?? '',
                        InfrastructureId: l.infrastructureId ?? l.InfrastructureId ?? null,
                        StartLng: derived.coords[0]?.[0],
                        StartLat: derived.coords[0]?.[1],
                        EndLng: derived.coords[1]?.[0],
                        EndLat: derived.coords[1]?.[1],
                        Angle: l.angle ?? l.Angle ?? null,
                        Length: l.length ?? l.Length ?? null,
                        UnitWidth: infra ? (infra.unitWidth ?? infra.UnitWidth ?? null) : 1,
                        SizeLength: infra ? (infra.sizeLength ?? infra.SizeLength ?? null) : 1,
                        Status: l.status ?? l.Status ?? null
                    }
                };
            })
            .filter(Boolean);
        return { type: 'FeatureCollection', features };
    };

    const loadLinesList = async (leaseId) => {
        try {
            console.log('rssr')
            const result = await getAPIDataParamsReturn('/Management/Lines', {
                includeInactive: true,
                includeRemoved: true,
                LeaseId: leaseId
            });
            console.log('rr')
            const list = result || [];
            setLinesList(list);
            setLineGeo(buildLineGeoFromList(list));
            if (selectedFarm?.warehouseId) {
                await loadGeoJsonOverlays(selectedFarm.warehouseId, leaseId);
            }
        } catch (e) {
            console.error(e);
            Swal.fire('Error', 'Unable to load existing lines for this lease.', 'error');
        }
    };

    const resetDrawing = () => {
        drawRef.current?.deleteAll();
        setMetrics(null);
        setSelectedLineId(null);
        setSelectedLineStatus(null);
        setLineNbr('');
        setLineName('');
        setAngleInput('');
        setLengthInput('');
        setStartLatInput('');
        setStartLngInput('');
        setOffsetDistanceInput('');
        setOffsetBearingInput('');
        setSelectedInfraId('');
        selectedFeatureIdRef.current = null;
    };

    const clearAllInputs = () => {
        resetDrawing();
        setLineGeo(EMPTY_FC);
        setGeoDisplayLines(EMPTY_FC);
        setGeoDisplayLabels(EMPTY_FC);
        setOffsetPreview(EMPTY_FC);
    };

    const computeMetricsFromCoords = (coords, infraId) => {
        if (!coords || coords.length < 2) return null;
        const start = coords[0];
        const end = coords[coords.length - 1];
        const lengthKm = turf.length(turf.lineString(coords), { units: 'kilometers' });
        const meters = Math.round(lengthKm * 1000);
        const bearing = (turf.bearing(start, end) + 360) % 360;
        const infra = infrastructure.find(i => String(i.id || i.Id) === String(infraId));
        const sizeLen = infra ? (infra.sizeLength || infra.SizeLength) : null;
        const unitWidth = infra ? (infra.unitWidth || infra.UnitWidth || 1) : 1;
        const units = sizeLen ? Math.max(1, Math.round((meters / sizeLen) * unitWidth)) : 0;
        return { meters, angle: bearing, units };
    };

    const normalizeAngle = (val) => {
        if (!Number.isFinite(val)) return 0;
        const normalized = val % 360;
        return normalized < 0 ? normalized + 360 : normalized;
    };

    const unitsToMeters = (unitsValue, infraId) => {
        if (!Number.isFinite(unitsValue) || unitsValue <= 0) return null;
        const infra = infrastructure.find(i => String(i.id || i.Id) === String(infraId));
        const sizeLen = infra ? (infra.sizeLength || infra.SizeLength || 1) : 1;
        const unitWidth = infra ? (infra.unitWidth || infra.UnitWidth || 1) : 1;
        return (unitsValue / unitWidth) * sizeLen;
    };

    const loadGeoJsonOverlays = async (warehouseId, leaseId) => {
        if (!warehouseId) return;
        try {
            const [lineData, labelData] = await Promise.all([
                getAPIDataParamsReturn('/GeoJSON/UnitLines', { warehouseId, leaseID: leaseId }),
                getAPIDataParamsReturn('/GeoJSON/UnitLabels', { warehouseId, leaseID: leaseId })
            ]);
            setGeoDisplayLines(lineData || EMPTY_FC);
            setGeoDisplayLabels(labelData || EMPTY_FC);
        } catch (e) {
            console.error(e);
        }
    };

    const loadLeaseOutlines = async (leaseList) => {
        try {
            const leaseIds = new Set(leaseList.map(l => Number(l.id)));
            const leaseData = await getAPIDataReturn('/GeoJSON/AllLeases');
            const matchedFeatures = leaseData.features.filter(f => {
                const fid = Number(f?.properties?.id);
                return leaseIds.has(fid);
            });
            setLeaseGeo(matchedFeatures.length > 0 ? { type: 'FeatureCollection', features: matchedFeatures } : EMPTY_FC);
        } catch (e) {
            console.error(e);
            setLeaseGeo(EMPTY_FC);
        }
    };

    const deriveCoordsFromLineTable = (line) => {
        if (!line) return null;
        const startLng = Number(line.lng ?? line.Lng);
        const startLat = Number(line.lat ?? line.Lat);
        if (!Number.isFinite(startLng) || !Number.isFinite(startLat)) {
            return null;
        }

        const infraId = line.infrastructureId ?? line.InfrastructureId;
        const unitsValue = Number(line.length ?? line.Length ?? 0);
        const metersRaw = unitsToMeters(unitsValue, infraId);
        const meters = Number.isFinite(metersRaw) ? Math.round(metersRaw) : null;
        const angle = normalizeAngle(Number(line.angle ?? line.Angle ?? 0));

        if (!meters || meters <= 0) {
            return null;
        }

        const dest = turf.destination([startLng, startLat], meters / 1000, angle);
        const end = dest?.geometry?.coordinates;
        if (!end) return null;

        return {
            coords: [[startLng, startLat], end],
            metrics: {
                meters,
                angle,
                units: Number.isFinite(unitsValue) ? unitsValue : 0,
                start: [startLng, startLat],
                end
            }
        };
    };

    const handleSelectLine = (lineId, featureOverride = null) => {
        // clear current drawing/selection visuals before loading the new line
        drawRef.current?.deleteAll();
        setMetrics(null);
        setAngleInput('');
        setLengthInput('');
        setSelectedLineId(lineId);
        setSelectedLineStatus(null);
        const selectedLine = linesList.find(l => String(l.id ?? l.Id) === String(lineId));
        setLineName(selectedLine?.name ?? selectedLine?.Name ?? '');
        setOffsetPreview(EMPTY_FC);
        setStartLatInput('');
        setStartLngInput('');

        let coords = null;
        let statusValue = null;
        const feature = featureOverride;
        if (feature) {
            const props = feature.properties || {};
            const startLng = Number(props.StartLng);
            const startLat = Number(props.StartLat);
            const endLng = Number(props.EndLng);
            const endLat = Number(props.EndLat);
            if (!coords || coords.length < 2) {
                if (!Number.isNaN(startLng) && !Number.isNaN(startLat) && !Number.isNaN(endLng) && !Number.isNaN(endLat)) {
                    coords = [[startLng, startLat], [endLng, endLat]];
                }
            }
            const featureAngle = Number(props.Angle);
            const featureLen = Number(props.Length);
            if (featureAngle && !Number.isNaN(featureAngle)) {
                setAngleInput(featureAngle.toFixed(2));
            }
            if (featureLen && !Number.isNaN(featureLen)) {
                setLengthInput(String(featureLen));
            }
            const infraProp = props.InfrastructureId;
            if (infraProp) {
                setSelectedInfraId(String(infraProp));
            }
            const statusProp = props.Status ?? props.status;
            if (statusProp !== undefined && statusProp !== null) {
                statusValue = Number(statusProp);
            }
            const lineNbrProp = props.LineNbr;
            setLineNbr(String(lineNbrProp));
            if (coords && coords.length >= 2 && drawRef.current) {
                setStartLngInput(String(coords[0][0]));
                setStartLatInput(String(coords[0][1]));
                const addedId = drawRef.current.add({
                    type: 'Feature',
                    properties: {
                        ...props,
                        lineId
                    },
                    geometry: { type: 'LineString', coordinates: coords }
                });
                const featureId = Array.isArray(addedId) ? addedId[0] : addedId;
                if (featureId) {
                    selectedFeatureIdRef.current = featureId;
                    drawRef.current.changeMode('direct_select', { featureId });
                }

            }
        }

        if (statusValue !== null && !Number.isNaN(statusValue)) {
            setSelectedLineStatus(statusValue);
        }

    };

    React.useEffect(() => {
        updateMetricsRef.current = updateMetricsFromFeature;
        handleSelectLineRef.current = handleSelectLine;
    }, [updateMetricsFromFeature, handleSelectLine]);

    const getDrawnFeature = () => {
        const data = drawRef.current?.getAll();
        if (!data || !data.features || data.features.length === 0) {
            return null;
        }
        const feature = data.features[0];
        if (feature.geometry?.type !== 'LineString' || feature.geometry.coordinates.length < 2) {
            return null;
        }
        return feature;
    };

    const recomputeOffsetPreview = React.useCallback(() => {
        if (!selectedLineId) {
            setOffsetPreview(EMPTY_FC);
            return;
        }

        const distanceVal = parseFloat(offsetDistanceInput);
        const bearingVal = parseFloat(offsetBearingInput);
        if (!Number.isFinite(distanceVal) || distanceVal <= 0 || !Number.isFinite(bearingVal)) {
            setOffsetPreview(EMPTY_FC);
            return;
        }

        const feature = getDrawnFeature();
        const start = feature?.geometry?.coordinates?.[0];
        if (!start) {
            setOffsetPreview(EMPTY_FC);
            return;
        }

        const offsetStart = turf.destination(start, distanceVal / 1000, bearingVal)?.geometry?.coordinates;
        if (!offsetStart) {
            setOffsetPreview(EMPTY_FC);
            return;
        }

        setOffsetPreview({
            type: 'FeatureCollection',
            features: [
                {
                    type: 'Feature',
                    geometry: {
                        type: 'LineString',
                        coordinates: [start, offsetStart]
                    },
                    properties: {}
                }
            ]
        });
    }, [offsetBearingInput, offsetDistanceInput, selectedLineId]);

    React.useEffect(() => {
        const feature = getDrawnFeature();
        if (feature) {
            updateMetricsRef.current?.(feature);
        }
    }, [selectedInfraId]);

    React.useEffect(() => {
        recomputeOffsetPreview();
    }, [recomputeOffsetPreview, angleInput, lengthInput, metrics, startLatInput, startLngInput]);

    const applyAngleLengthAdjustments = () => {
        if (!drawRef.current) return;
        const featureId = selectedFeatureIdRef.current || drawRef.current.getAll()?.features?.[0]?.id;
        if (!featureId) return;
        const feature = drawRef.current.get(featureId);
        if (!feature || feature.geometry?.type !== 'LineString' || feature.geometry.coordinates.length < 1) return;
        const fallbackStart = feature.geometry.coordinates[0];
        const parsedLng = parseFloat(startLngInput);
        const parsedLat = parseFloat(startLatInput);
        const start = Number.isFinite(parsedLng) && Number.isFinite(parsedLat) ? [parsedLng, parsedLat] : fallbackStart;
        if (!start || start.length < 2) return;

        const unitsInput = parseFloat(lengthInput);
        const angleVal = parseFloat(angleInput);
        const angle = Number.isFinite(angleVal) ? angleVal : (metrics?.angle ?? 0);
        const metersFromUnits = Number.isFinite(unitsInput) && unitsInput > 0
            ? unitsToMeters(unitsInput, selectedInfraId)
            : metrics?.meters;

        if (!Number.isFinite(angle) || !Number.isFinite(metersFromUnits) || metersFromUnits <= 0) return;

        const dest = turf.destination(start, metersFromUnits / 1000, angle);
        const end = dest?.geometry?.coordinates;
        if (!end) return;

        const coords = [start, end];

        const existing = drawRef.current.get(featureId);
        if (existing) {
            drawRef.current.delete(featureId);
            const newId = drawRef.current.add({
                id: featureId,
                type: 'Feature',
                properties: existing.properties || {},
                geometry: { type: 'LineString', coordinates: coords }
            });
            const finalId = Array.isArray(newId) ? newId[0] : newId || featureId;
            selectedFeatureIdRef.current = finalId;
            drawRef.current.changeMode('direct_select', { featureId: finalId });
        }

        const calc = computeMetricsFromCoords(coords, selectedInfraId);
        if (calc) {
            setMetrics({ ...calc, start, end });
            if (Number.isFinite(unitsInput)) {
                setLengthInput(String(unitsInput));
            }
            setStartLngInput(String(start[0]));
            setStartLatInput(String(start[1]));
        }
    };

    const handleCreateOffsetLine = () => {
        if (!canEditWarehouse) {
            Swal.fire('Access denied', 'You do not have permission to modify this warehouse.', 'info');
            return;
        }
        if (!selectedLineId) {
            Swal.fire('Select a line', 'Pick an existing line first.', 'info');
            return;
        }
        if (!selectedInfraId) {
            Swal.fire('Select infrastructure', 'Choose infrastructure before creating an offset line.', 'info');
            return;
        }

        const feature = getDrawnFeature();
        if (!feature) {
            Swal.fire('Drawn line missing', 'Load an existing line on the map first.', 'info');
            return;
        }

        const offsetMeters = parseFloat(offsetDistanceInput);
        const offsetBearing = parseFloat(offsetBearingInput);
        if (!Number.isFinite(offsetMeters) || offsetMeters <= 0 || !Number.isFinite(offsetBearing)) {
            Swal.fire('Offset required', 'Enter a positive offset distance and bearing.', 'warning');
            return;
        }

        const start = feature.geometry.coordinates[0];
        const offsetStart = turf.destination(start, offsetMeters / 1000, offsetBearing)?.geometry?.coordinates;
        if (!offsetStart) {
            Swal.fire('Offset failed', 'Could not compute the offset start point.', 'error');
            return;
        }

        const angleValRaw = parseFloat(angleInput);
        const unitsValRaw = parseFloat(lengthInput);
        const angleVal = Number.isFinite(angleValRaw) ? angleValRaw : metrics?.angle;
        const lengthMeters = Number.isFinite(unitsValRaw)
            ? unitsToMeters(unitsValRaw, selectedInfraId)
            : metrics?.meters;

        if (!Number.isFinite(angleVal) || !Number.isFinite(lengthMeters) || lengthMeters <= 0) {
            Swal.fire('Missing metrics', 'Angle and length/units are required to create the new line.', 'warning');
            return;
        }

        const offsetEnd = turf.destination(offsetStart, lengthMeters / 1000, angleVal)?.geometry?.coordinates;
        if (!offsetEnd) {
            Swal.fire('Offset failed', 'Could not compute the offset end point.', 'error');
            return;
        }

        const coords = [offsetStart, offsetEnd];
        drawRef.current?.deleteAll();
        const addedId = drawRef.current?.add({
            type: 'Feature',
            properties: {},
            geometry: { type: 'LineString', coordinates: coords }
        });
        const featureId = Array.isArray(addedId) ? addedId[0] : addedId;
        if (featureId) {
            selectedFeatureIdRef.current = featureId;
            drawRef.current?.changeMode('direct_select', { featureId });
        }

        const calc = computeMetricsFromCoords(coords, selectedInfraId);
        if (calc) {
            setMetrics({ ...calc, start: offsetStart, end: offsetEnd });
            setAngleInput(Number.isFinite(angleVal) ? angleVal.toFixed(2) : '');
            if (Number.isFinite(unitsValRaw)) {
                setLengthInput(String(unitsValRaw));
            }
        }

        setSelectedLineId(null);
        setSelectedLineStatus(null);
        setLineNbr(String(suggestNextLineNbr(linesList)));
        setLineName('');
        setOffsetPreview(EMPTY_FC);
    };

    React.useEffect(() => {
        // Keep the drawn line in sync as angle/units inputs change
        applyAngleLengthAdjustments();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [angleInput, lengthInput, selectedInfraId, startLatInput, startLngInput]);

    const handleSave = async (mode) => {
        if (!canEditWarehouse) {
            Swal.fire('Access denied', 'You do not have permission to modify this warehouse.', 'info');
            return;
        }
        if (!selectedFarm?.warehouseId || !selectedLeaseId || !selectedInfraId) {
            Swal.fire('Missing data', 'Select a farm, lease, and infrastructure first.', 'warning');
            return;
        }

        const feature = getDrawnFeature();
        if (!feature) {
            Swal.fire('Draw a line', 'Use the draw tool to sketch the line first.', 'info');
            return;
        }


        setSaving(true);

        const angleVal = parseFloat(angleInput);
        const unitsVal = parseFloat(lengthInput);
        const infraObj = infrastructure.find(i => String(i.id || i.Id) === String(selectedInfraId));
        const infraLen = infraObj ? (infraObj.sizeLength || infraObj.SizeLength || 0) : 0;
        const unitWidthVal = infraObj ? (infraObj.unitWidth || infraObj.UnitWidth || 1) : 1;
        const metersFromUnits = Number.isFinite(unitsVal) && unitsVal > 0 && infraLen > 0
            ? (unitsVal / unitWidthVal) * infraLen
            : undefined;
        const unitLengthOverride = Number.isFinite(unitsVal) && unitsVal > 0 ? Math.round(unitsVal) : undefined;

        const body = {
            leaseId: Number(selectedLeaseId),
            infrastructureId: Number(selectedInfraId),
            coordinates: feature.geometry.coordinates,
            lineNbr: lineNbr ? Number(lineNbr) : null,
            name: lineName.trim() || null,
            angle: Number.isFinite(angleVal) ? angleVal : undefined,
            distanceMeters: Number.isFinite(metersFromUnits) ? metersFromUnits : undefined,
            unitLength: unitLengthOverride
        };

        try {
            if (mode === 'create') {
                await postApiJson('/Management/LineDesigner', body);
                await loadLinesList(selectedLeaseId);
                resetDrawing();
                Swal.fire('Saved', 'Line created.', 'success');
            } else if (mode === 'update') {
                if (!selectedLineId) {
                    Swal.fire('Select a line', 'Pick an existing line to update.', 'info');
                    setSaving(false);
                    return;
                }
                await putApiJson(`/Management/LineDesigner/${selectedLineId}`, body);
                await loadLinesList(selectedLeaseId);
                resetDrawing();
                Swal.fire('Updated', 'Line updated.', 'success');
            }

        } catch (e) {
            console.error(e);
            Swal.fire('Error', 'Failed to save line changes.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!canEditWarehouse) {
            Swal.fire('Access denied', 'You do not have permission to modify this warehouse.', 'info');
            return;
        }
        if (!selectedLineId) {
            Swal.fire('Select a line', 'Choose a line to delete.', 'info');
            return;
        }
        const result = await Swal.fire({
            title: 'Delete line?',
            text: 'Are you sure you want to delete this line? It will no longer be visible.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete',
            cancelButtonText: 'Cancel'
        });
        if (!result.isConfirmed) return;

        setSaving(true);
        try {
            await postApi('/Farms/Line/UpdateLineStatus', { LineId: selectedLineId, Status: 2 });
            await loadLinesList(selectedLeaseId);
            resetDrawing();
            Swal.fire('Deleted', 'Line removed.', 'success');
        } catch (e) {
            console.error(e);
            Swal.fire('Error', e.message || 'Failed to delete line.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleStatusUpdate = async () => {
        if (!canEditWarehouse) {
            Swal.fire('Access denied', 'You do not have permission to modify this warehouse.', 'info');
            return;
        }
        if (!selectedLineId) {
            Swal.fire('Select a line', 'Choose a line to update.', 'info');
            return;
        }

        const fallbackStatus = () => {
            const match = linesList.find(l => String(l.id || l.Id) === String(selectedLineId));
            const statusVal = match ? (match.status ?? match.Status) : null;
            const numVal = Number(statusVal);
            return Number.isFinite(numVal) ? numVal : null;
        };

        const currentStatus = Number.isFinite(Number(selectedLineStatus)) ? Number(selectedLineStatus) : fallbackStatus();
        const nextStatus = currentStatus === 1 ? 0 : 1; // 0 = in use, 1 = not in use
        setSaving(true);
        try {
            await postApi('/Farms/Line/UpdateLineStatus', { LineId: selectedLineId, Status: nextStatus });
            await loadLinesList(selectedLeaseId);
            setSelectedLineStatus(nextStatus);
            Swal.fire('Updated', nextStatus === 1 ? 'Line marked not in use.' : 'Line marked in use.', 'success');
        } catch (e) {
            console.error(e);
            Swal.fire('Error', e.message || 'Failed to update line status.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const suggestNextLineNbr = (list) => {
        if (!list || list.length === 0) return 1;
        const nums = list.map(l => l.lineNbr || l.LineNbr || 0);
        return Math.max(...nums) + 1;
    };

    // Determine if the selected line can be modified (status toggle / delete)
    // Rules:
    // - If `geoDisplayLines` is empty, do NOT allow modifications.
    // - If any feature in `geoDisplayLines` matches the selected line id and has GroupId != '0',
    //   then the line contains stock and cannot be modified.
    const canModifySelectedLine = React.useMemo(() => {
        if (!selectedLineId) return false;
        const features = geoDisplayLines?.features;
        if (!features || features.length === 0) return false; // overlays not loaded or empty => do not allow
        const hasStock = features.some(f => {
            if (!f || !f.properties) return false;
            const fid = f.properties.LineId ?? f.properties.RowId ?? f.properties.LineRefId;
            if (String(fid) !== String(selectedLineId)) return false;
            const groupId = f.properties.GroupId ?? f.properties.groupId ?? null;
            return groupId !== null && String(groupId) !== '0';
        });
        return !hasStock;
    }, [geoDisplayLines, selectedLineId]);

    if (!canAccess) {
        return <Typography variant="h4">You are not authorized to view this page.</Typography>;
    }

    return (
        <Stack spacing={2} sx={{ height: 'calc(100vh - 120px)', minHeight: 560 }}>
            <Typography variant="h4">Line Designer</Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'flex-start' }} sx={{ flex: 1, minHeight: 0 }}>
                <Card sx={{ width: { xs: '100%', md: 360 }, flexShrink: 0, height: { xs: 'auto', md: '100%' }, overflow: 'auto' }}>
                    <CardContent>
                        <Stack spacing={2}>
                            <Typography variant="h6">Settings</Typography>
                            {loadingLookups ? <CircularProgress size={24} /> : null}

                            <SelectFarmWarehouse
                                state={selectedFarm}
                                setState={setSelectedFarm}
                                access={access}
                                disabled={saving}
                                hasEditAccess={setCanEditWarehouse}
                            />

                            <FormControl fullWidth size="small" disabled={saving}>
                                <InputLabel id="lease-label">Lease</InputLabel>
                                <Select
                                    labelId="lease-label"
                                    label="Lease"
                                    value={selectedLeaseId}
                                    onChange={(e) => {
                                        clearAllInputs();
                                        setSelectedLeaseId(e.target.value);
                                        setSelectedLineId(null);
                                    }}
                                >
                                    {leases.map(l => (
                                        <MenuItem key={l.id || l.Id} value={String(l.id || l.Id)}>
                                            {`${l.name || l.Name || ''}${(l.leaseNo || l.LeaseNo || l.number || l.Number) ? ` - ${l.leaseNo || l.LeaseNo || l.number || l.Number}` : ''}`}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl fullWidth size="small" disabled={saving}>
                                <InputLabel id="infra-label">Infrastructure</InputLabel>
                                <Select
                                    labelId="infra-label"
                                    label="Infrastructure"
                                    value={selectedInfraId}
                                    onChange={(e) => setSelectedInfraId(e.target.value)}
                                >
                                    {infrastructure.map(infra => (
                                        <MenuItem key={infra.id || infra.Id} value={String(infra.id || infra.Id)}>
                                            {infra.description || infra.Description}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <Stack spacing={0.5}>
                                <Typography variant="subtitle2">Existing lines</Typography>
                                <Typography variant="body2">Click a line or label on the map to load and edit it.</Typography>
                                {selectedLineId ? (
                                    <Typography variant="body2">Selected line #{lineNbr || selectedLineId}</Typography>
                                ) : null}
                            </Stack>

                            <TextField
                                size="small"
                                label="Line number"
                                value={lineNbr}
                                onChange={(e) => setLineNbr(e.target.value)}
                                disabled={saving}
                            />

                            <TextField
                                size="small"
                                label="Tank Name (Hatchery Only)"
                                value={lineName}
                                onChange={(e) => setLineName(e.target.value)}
                                inputProps={{ maxLength: 100 }}
                                disabled={saving}
                            />

                            <Divider />

                            <Stack spacing={1}>
                                <Typography variant="subtitle2">Metrics</Typography>
                                {metrics ? (
                                    <>
                                        <Typography variant="body2">Length: {metrics.meters} m</Typography>
                                        <Typography variant="body2">Units (by infra): {metrics.units}</Typography>
                                        <Typography variant="body2">Angle: {metrics.angle.toFixed(2)}°</Typography>
                                    </>
                                ) : (
                                    <Typography variant="body2">Draw a line to see length and units.</Typography>
                                )}
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                    <TextField
                                        size="small"
                                        label="Angle (deg)"
                                        type="number"
                                        value={angleInput}
                                        onChange={(e) => setAngleInput(e.target.value)}
                                        onBlur={applyAngleLengthAdjustments}
                                        onKeyDown={(e) => { if (e.key === 'Enter') applyAngleLengthAdjustments(); }}
                                        inputProps={{ step: '0.1' }}
                                        fullWidth
                                        disabled={saving}
                                    />
                                    <TextField
                                        size="small"
                                        label="Units"
                                        type="number"
                                        value={lengthInput}
                                        onChange={(e) => setLengthInput(e.target.value)}
                                        onBlur={applyAngleLengthAdjustments}
                                        onKeyDown={(e) => { if (e.key === 'Enter') applyAngleLengthAdjustments(); }}
                                        inputProps={{ step: String(selectedInfraUnitWidth) }}
                                        fullWidth
                                        disabled={saving}
                                    />
                                </Stack>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                    <TextField
                                        size="small"
                                        label="Start latitude"
                                        type="number"
                                        value={startLatInput}
                                        onChange={(e) => setStartLatInput(e.target.value)}
                                        onBlur={applyAngleLengthAdjustments}
                                        onKeyDown={(e) => { if (e.key === 'Enter') applyAngleLengthAdjustments(); }}
                                        inputProps={{ step: '0.000001' }}
                                        fullWidth
                                        disabled={saving}
                                    />
                                    <TextField
                                        size="small"
                                        label="Start longitude"
                                        type="number"
                                        value={startLngInput}
                                        onChange={(e) => setStartLngInput(e.target.value)}
                                        onBlur={applyAngleLengthAdjustments}
                                        onKeyDown={(e) => { if (e.key === 'Enter') applyAngleLengthAdjustments(); }}
                                        inputProps={{ step: '0.000001' }}
                                        fullWidth
                                        disabled={saving}
                                    />
                                </Stack>
                            </Stack>
                            <Stack direction="row" spacing={1}>
                                <Button variant="outlined" onClick={resetDrawing} fullWidth disabled={saving}>Clear</Button>
                                {canEditWarehouse ? (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        disabled={saving}
                                        onClick={() => handleSave(selectedLineId ? 'update' : 'create')}
                                        fullWidth
                                    >
                                        {saving ? <CircularProgress size={18} /> : selectedLineId ? 'Update line' : 'Create line'}
                                    </Button>
                                ) : null}
                            </Stack>
                            {selectedLineId && canModifySelectedLine && canEditWarehouse ? (
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                    <Button
                                        variant="outlined"
                                        color="warning"
                                        disabled={saving}
                                        onClick={handleStatusUpdate}
                                        fullWidth
                                    >
                                        {selectedLineStatus === 1 ? 'Mark in use' : 'Mark not in use'}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        disabled={saving}
                                        onClick={handleDelete}
                                        fullWidth
                                    >
                                        Delete
                                    </Button>
                                </Stack>
                            ) : null}
                            {selectedLineId && canEditWarehouse ? (
                                <Stack spacing={1}>
                                    <Divider />
                                    <Typography variant="subtitle2">Create offset line</Typography>
                                    <Typography variant="body2">Use the selected line to start a new line offset by distance and bearing.</Typography>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                        <TextField
                                            size="small"
                                            label="Offset distance (m)"
                                            type="number"
                                            value={offsetDistanceInput}
                                            onChange={(e) => setOffsetDistanceInput(e.target.value)}
                                            inputProps={{ step: '1', min: '0' }}
                                            fullWidth
                                            disabled={saving}
                                        />
                                        <TextField
                                            size="small"
                                            label="Offset bearing (deg)"
                                            type="number"
                                            value={offsetBearingInput}
                                            onChange={(e) => setOffsetBearingInput(e.target.value)}
                                            inputProps={{ step: '0.1' }}
                                            fullWidth
                                            disabled={saving}
                                        />
                                    </Stack>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        onClick={handleCreateOffsetLine}
                                        disabled={saving}
                                    >
                                        Start new line from offset
                                    </Button>
                                </Stack>
                            ) : null}



                        </Stack>
                    </CardContent>
                </Card>

                <Box sx={{ flex: 1, position: 'relative', minHeight: { xs: 420, md: 'calc(100vh - 220px)' } }}>
                    {!mapReady ? <CircularProgress sx={{ position: 'absolute', top: 12, left: 12, zIndex: 2 }} /> : null}
                    <Box ref={mapNodeRef} sx={{ position: 'absolute', inset: 0, minHeight: { xs: 420, md: 'calc(100vh - 220px)' } }} />
                </Box>
            </Stack>
        </Stack>
    );
};

export default LineDesigner;